import { Box, Paper, Typography, Stack, alpha, useTheme, Chip, Button, IconButton, Tooltip, Divider } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuBuilding, LuBan, LuFileText, LuShare2 } from "react-icons/lu"; // LuShare2 for consistency
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../components/button/RefreshButton";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogGeneral from "../../components/dialog/DialogGeneral";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Company } from "../../db_access/company";
import { TemplateSharing } from "../../db_access/template_sharing";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import dayjs from "dayjs";

const CompanyPrebuiltTemplateGrid = ({ selectedCompanyID, type }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const { refresh, setRefresh, columnVisibilities, enqueueAlert } = useContextProvider();

    const gridRef = useRef(null);
    const dialogRef = useRef(null);
    const gridHeaderContainerRef = useRef(null);

    const [templates, setTemplates] = useState([]);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [searchText, setSearchText] = useState("");
    const colVisibility_key = "company_prebuilt_template";
    const [columnMenuVisible, setColumnMenuVisible] = useState(false);
    const [companyName, setCompanyName] = useState("");

    const activeColor = '#0ea5e9'; // Standardized Blue for Company Templates

    useEffect(() => {
        if (selectedCompanyID) {
            Company.get_public_info(selectedCompanyID)
                .then((res) => res.json())
                .then((data) => setCompanyName(data?.[0]?.name || t("Unknown Organization")));
        }
    }, [selectedCompanyID, t]);

    const colDefs = useMemo(() => {
        const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
        const types = {
            product: t("Product"),
            project: t("Project"),
            building: t("Building"),
            supply_chain: t("Supply Chain"),
            structure: t("Organization"),
        };

        return [
            {
                headerName: t("Name"),
                field: "name",
                ...defaultColDef,
                ...centerAlignColDef, // Match user request for centered headers/content
                flex: 1.5,
                cellStyle: { ...tok.gridCellText, fontWeight: 500 }
            },
            {
                headerName: t("Type"),
                field: "type",
                ...defaultColDef,
                ...centerAlignColDef,
                width: 150,
                valueGetter: (p) => types[p.data?.type] ?? p.data?.type ?? "",
                cellRenderer: p => {
                    const label = types[p.data?.type] ?? p.data?.type ?? "";
                    return (
                        <Typography sx={{
                            ...tok.gridCellText,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: tok.text.primary,
                            textTransform: 'capitalize',
                            letterSpacing: '0.02em'
                        }}>
                            {label}
                        </Typography>
                    );
                }
            },
            { headerName: t("Year"), field: "year", ...defaultColDef, ...centerAlignColDef, width: 100 },
            { headerName: t("GWP (kg CO₂e)"), field: "gwp", ...defaultColDef, ...centerAlignColDef, ...floatFormater, width: 160 },
            { headerName: t("Date Modified"), field: "date_modified", ...defaultColDef, ...centerAlignColDef, width: 180 },
        ].map((colDef) => ({
            ...colDef,
            hide: isHidden(colDef.field),
            headerClass: 'ag-header-cell-centered'
        }));
    }, [columnVisibilities, t, tok.gridCellText]);

    const defaultCols = useMemo(() => colDefs.map((col) => col.field), [colDefs]);

    const displayedTemplates = useMemo(() => {
        if (type) return templates.filter((template) => template.type === type);
        return templates;
    }, [templates, type]);

    useEffect(() => {
        if (selectedCompanyID) {
            TemplateSharing.get_by_company_id(selectedCompanyID)
                .then((res) => res.json())
                .then((data) => setTemplates(data.map((item, index) => ({ ...item, id: index }))));
        }
    }, [refresh, selectedCompanyID]);

    const stopSharingToCompany = useCallback(
        (selectedTemplates) => {
            TemplateSharing.delete(
                selectedTemplates.map((s) => ({
                    company_id: selectedCompanyID,
                    product_id: s.product_id,
                    project_id: s.project_id,
                    supply_chain_id: s.supply_chain_id,
                    structure_id: s.structure_id,
                }))
            ).then(() => {
                gridRef.current?.api?.deselectAll();
                enqueueAlert({ message: "Sharing stopped successfully", severity: "success" });
                setRefresh((r) => !r);
            }).catch(() => enqueueAlert({ message: "Failed to stop sharing", severity: "error" }));
        },
        [setRefresh, selectedCompanyID, enqueueAlert]
    );

    const contextMenuOptions = useMemo(
        () => [selectedCompanyID && { label: t("Stop sharing (Bulk)"), action: () => dialogRef.current?.open(), needSelectedItems: true }].filter(Boolean),
        [selectedCompanyID, t]
    );

    return (
        <Box>
            <Paper variant="Table Container" sx={{ mt: tok.spacing.pageGap }}>
                <TableHeaderContainer>
                    <TableTitleContainer>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '8px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha(activeColor, 0.1), color: activeColor
                            }}>
                                <LuBuilding size={18} />
                            </Box>
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography sx={tok.tableTitle}>
                                    {companyName} {t("Library")} ({displayedTemplates.length})
                                </Typography>
                            </Box>
                        </Stack>
                    </TableTitleContainer>

                    <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
                        <Box sx={{ width: 350 }}>
                            <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search organization...")} width={350} />
                        </Box>

                        {selectedTemplates.length > 0 && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<LuBan size={16} />}
                                onClick={() => dialogRef.current?.open()}
                                sx={{
                                    height: 40,
                                    bgcolor: theme.palette.error.main,
                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.9) },
                                    borderRadius: tok.radius.button,
                                    fontWeight: 700, textTransform: 'none', px: 2
                                }}
                            >
                                {t("Stop Sharing")} ({selectedTemplates.length})
                            </Button>
                        )}

                        <ExportAsXLSXButton gridRef={gridRef} filename={`${companyName}_templates`} />
                        <ColumnVisibilityButton
                            columns={colDefs}
                            visibleColumns={columnVisibilities[colVisibility_key] || defaultCols}
                            columnVis_key={colVisibility_key}
                        />
                        <RefreshButton onRefresh={() => setRefresh((r) => !r)} />
                    </TableHeaderToolsContainer>
                </TableHeaderContainer>

                <Box>
                    <AgGrid
                        rowData={displayedTemplates}
                        columnDefs={colDefs}
                        onSelectionChanged={(e) => setSelectedTemplates(e.api.getSelectedRows())}
                        handleContextMenu={(e) => {
                            e.preventDefault(); setMenuPosition({ x: e.pageX, y: e.pageY }); setMenuVisible(true);
                        }}
                        quickFilterText={searchText}
                        ref={gridRef}
                        domLayout="autoHeight"
                    />
                </Box>
            </Paper>

            <ColumnMenu
                anchorRef={gridHeaderContainerRef}
                visible={columnMenuVisible}
                onClose={() => setColumnMenuVisible(false)}
                colDefs={colDefs}
                colVisibility_key={colVisibility_key}
                defaultCols={defaultCols}
            />

            <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={() => setMenuVisible(false)} selectedItems={selectedTemplates} options={contextMenuOptions} />

            <DialogGeneral
                selectedItems={selectedTemplates}
                handleAction={stopSharingToCompany}
                ref={dialogRef}
                header={t("Confirm Stop Share")}
                title={t("Are you sure you want to stop sharing these templates with this organization?")}
                actionName={t("Stop Share")}
                actionColor="error"
            />
        </Box>
    );
};

export default CompanyPrebuiltTemplateGrid;
